import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika } from '../../../../styles/theme';
import { formatWeekdayDay } from '../../../../utils/dateLocale';
import { addDays } from '../../../../utils/localDate';
import { HORIZON_DAYS } from '../../../../services/horizonDays';

interface Props {
  /** First day of the seven the gesture plans. */
  from: string;
  busy: boolean;
  onPress: () => void;
}

/**
 * "Planifier les 7 jours suivants" — the end of the phone's one scroll (#722).
 *
 * It replaces WeekChainCard, which was a door to another page: « Planifier la
 * semaine du 28 septembre » switched the screen to next week and generated a
 * Monday-to-Sunday plan there. Now the dinners appear right here, under the
 * last one, and the cook keeps scrolling. The range is always named, because
 * "the next 7 days" means nothing without dates.
 */
export function ExtendPlanCard({ from, busy, onPress }: Props) {
  const { t, i18n } = useTranslation();
  const to = addDays(from, HORIZON_DAYS - 1);
  const range = t('thisWeek.extend.range', {
    from: formatWeekdayDay(from, i18n.language),
    to: formatWeekdayDay(to, i18n.language),
  });

  return (
    <Pressable
      style={({ pressed }) => [styles.card, pressed && !busy && styles.cardPressed]}
      onPress={busy ? undefined : onPress}
      disabled={busy}
      accessibilityRole="button"
      accessibilityState={{ busy }}
      accessibilityLabel={`${t('thisWeek.extend.title')} — ${range}`}
      testID="extend-plan"
    >
      <View style={styles.text}>
        <Text style={styles.title}>{busy ? t('thisWeek.extend.busy') : t('thisWeek.extend.title')}</Text>
        <Text style={styles.range} numberOfLines={1}>{range}</Text>
      </View>
      <View style={styles.go}>
        {busy ? (
          <ActivityIndicator size="small" color={Paprika.onPrimary} />
        ) : (
          <Feather name="plus" size={18} color={Paprika.onPrimary} />
        )}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginHorizontal: 16,
    marginTop: 16,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 16,
    backgroundColor: Paprika.surfaceLowest,
    shadowColor: 'rgba(82, 68, 60, 0.08)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 2,
  },
  cardPressed: {
    backgroundColor: Paprika.brandTint,
  },
  text: {
    flex: 1,
    gap: 2,
  },
  title: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: Paprika.primary,
  },
  range: {
    fontFamily: 'Inter-Regular',
    fontSize: 12.5,
    color: Paprika.textSecondary,
  },
  go: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.primary,
  },
});
